import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from 'src/components/ui/dialog';
import { Button } from 'src/components/ui/button';
import { Input } from 'src/components/ui/input';
import { Label } from 'src/components/ui/label';
import { Checkbox } from 'src/components/ui/checkbox';
import useCreateCompany, {
  CreateCompanyData,
} from 'src/api/core/companies/useCreateCompany';
import useUpdateCompany, {
  UpdateCompanyData,
} from 'src/api/core/companies/useUpdateCompany';
import { CompanyDTO } from 'src/api/core/companies/useCompany';

// Available modules (matching CompanyModulesTab.tsx)
const MODULE_TYPES = {
  REBATES: 'rebates',
} as const;

interface CompanyCreateModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: (company: CompanyDTO) => void;
  company?: CompanyDTO; // If provided, this is an edit modal
  module?: string;
}

const CompanyCreateModal: React.FC<CompanyCreateModalProps> = ({
  open,
  onClose,
  onSuccess,
  company,
  module,
}) => {
  const isEditing = !!company;

  // Determine initial modules - for editing use company's modules, for creating use module prop if available
  const getInitialModules = React.useCallback(() => {
    if (isEditing) {
      return company?.enabledModules || [];
    }
    // For new companies, default-check the module if it's passed and exists in MODULE_TYPES
    if (module && (Object.values(MODULE_TYPES) as string[]).includes(module)) {
      return [module];
    }
    return [];
  }, [isEditing, company?.enabledModules, module]);

  const [formData, setFormData] = useState<CreateCompanyData>({
    name: company?.name || '',
    email: company?.email || '',
    modules: getInitialModules(),
  });

  const [selectedModules, setSelectedModules] = useState<Set<string>>(
    new Set(getInitialModules()),
  );

  const createCompanyMutation = useCreateCompany();
  const updateCompanyMutation = useUpdateCompany(company?.id || '');

  const resetForm = () => {
    const initialModules = getInitialModules();
    setFormData({
      name: '',
      email: '',
      modules: initialModules,
    });
    setSelectedModules(new Set(initialModules));
  };

  // Reset form when modal opens or module prop changes
  useEffect(() => {
    if (open && !isEditing) {
      const initialModules = getInitialModules();
      setFormData({
        name: '',
        email: '',
        modules: initialModules,
      });
      setSelectedModules(new Set(initialModules));
    }
  }, [open, isEditing, getInitialModules]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const submitData = {
        ...formData,
        modules: Array.from(selectedModules),
      };

      if (isEditing) {
        const updateData: UpdateCompanyData = {
          name: submitData.name,
          email: submitData.email || undefined,
          // Note: Module updates would need to be handled separately via modules API
        };
        const response = await updateCompanyMutation.mutateAsync(updateData);
        if (response?.data?.company) {
          onSuccess?.(response.data.company);
        }
        onClose();
        resetForm();
      } else {
        const response = await createCompanyMutation.mutateAsync(submitData);
        if (response?.data?.company) {
          onSuccess?.(response.data.company);
        }
        onClose();
        resetForm();
      }
    } catch (error) {
      // Error handling is done by the mutation hooks
      console.error('Error saving company:', error);
    }
  };

  const handleClose = () => {
    onClose();
    if (!isEditing) {
      resetForm();
    }
  };

  const isLoading =
    createCompanyMutation.isLoading || updateCompanyMutation.isLoading;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? 'Edit Company' : 'Create New Company'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium">Basic Information</h3>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Company Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  placeholder="Enter company name"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                  placeholder="Enter company email"
                />
              </div>
            </div>
          </div>

          {/* Modules */}
          {!isEditing && (
            <div className="space-y-4">
              <h3 className="text-lg font-medium">Enable Modules</h3>
              <div className="space-y-3">
                {Object.entries(MODULE_TYPES).map(([key, value]) => (
                  <div
                    key={key}
                    className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg"
                  >
                    <Checkbox
                      checked={selectedModules.has(value)}
                      onCheckedChange={(checked) => {
                        const newSelectedModules = new Set(selectedModules);
                        if (checked) {
                          newSelectedModules.add(value);
                        } else {
                          newSelectedModules.delete(value);
                        }
                        setSelectedModules(newSelectedModules);
                      }}
                    />
                    <Label className="flex-1">
                      <div>
                        <div className="text-sm font-medium">
                          {key.charAt(0).toUpperCase() +
                            key.slice(1).toLowerCase()}
                        </div>
                        <div className="text-xs text-gray-600">
                          {value === 'rebates'
                            ? 'Enable rebate agreements and calculations for this company'
                            : 'Module functionality'}
                        </div>
                      </div>
                    </Label>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end space-x-2">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading
                ? 'Saving...'
                : isEditing
                  ? 'Update Company'
                  : 'Create Company'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CompanyCreateModal;
